import { V3 } from "libs/mjs";
import memoize from "lodash-es/memoize";
import memoizeOne from "memoize-one";
import messages from "messages";
import type {
  AdditionalCoordinate,
  AnnotationLayerDescriptor,
  APIAnnotation,
  APIAnnotationInfo,
  APIDataLayer,
  APIDataset,
  APISegmentationLayer,
  ServerTracing,
  ServerVolumeTracing,
} from "types/api_types";
import Constants, {
  type ContourMode,
  MAX_MAG_FOR_AGGLOMERATE_MAPPING,
  MappingStatusEnum,
  type Vector3,
  type Vector4,
} from "viewer/constants";
import { reuseInstanceOnEquality } from "viewer/model/accessors/accessor_helpers";
import {
  getDataLayers,
  getLayerByName,
  getMagInfo,
  getMappingInfo,
  getSegmentationLayerByName,
  getSegmentationLayers,
  getVisibleSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import {
  getActiveMagIndexForLayer,
  getAdditionalCoordinatesAsString,
  getFlooredPosition,
} from "viewer/model/accessors/flycam_accessor";
import { AnnotationTool, type AnnotationToolId } from "viewer/model/accessors/tool_accessor";
import { MAX_ZOOM_STEP_DIFF } from "viewer/model/bucket_data_handling/loading_strategy_logic";
import { jsConvertCellIdToRGBA } from "viewer/shaders/segmentation.glsl";
import { jsRgb2hsl } from "viewer/shaders/utils.glsl";
import { Store } from "viewer/singletons";
import type {
  ActiveMappingInfo,
  LabelAction,
  SegmentGroup,
  SegmentJournalEntry,
  SegmentMap,
  StoreAnnotation,
  VolumeTracing,
  WebknossosState,
} from "viewer/store";
import {
  getGroupByIdWithSubgroups,
  MISSING_GROUP_ID,
} from "viewer/view/right_border_tabs/shared/tree_hierarchy_view_helpers";
import { setSelectedSegmentsOrGroupAction } from "../actions/volumetracing_actions";
import { MagInfo } from "../helpers/mag_info";

export function getVolumeTracings(annotation: StoreAnnotation): Array<VolumeTracing> {
  return annotation.volumes;
}

export function getVolumeTracingById(
  annotation: StoreAnnotation,
  tracingId: string,
): VolumeTracing {
  const volumeTracing = annotation.volumes.find((t) => t.tracingId === tracingId);

  if (volumeTracing == null) {
    throw new Error(
      `Could not find volume tracing with id ${tracingId}. Only found: ${annotation.volumes.map((t) => t.tracingId)}`,
    );
  }

  return volumeTracing;
}

export function isTracingLayerWithoutFallback(segmentationLayer: APIDataLayer): boolean {
  const isTracingLayer = "tracingId" in segmentationLayer && segmentationLayer.tracingId != null;
  const hasFallbackLayer =
    "fallbackLayer" in segmentationLayer && segmentationLayer.fallbackLayer != null;
  return isTracingLayer && !hasFallbackLayer;
}

export function getVolumeTracingLayers(dataset: APIDataset): Array<APISegmentationLayer> {
  const layers = getSegmentationLayers(dataset);
  return layers.filter((layer) => layer.tracingId != null);
}

export function getVolumeTracingByLayerName(
  annotation: StoreAnnotation,
  layerName: string,
): VolumeTracing | null | undefined {
  // Given a segmentation layer, there might be a corresponding volume tracing. In that case,
  // the layer name will be the tracing id.
  const volumeTracing = annotation.volumes.find((t) => t.tracingId === layerName);
  return volumeTracing;
}

export function getVolumeTracingByNameOrActive(volumeLayerName: string | undefined) {
  return volumeLayerName
    ? getVolumeTracingByLayerName(Store.getState().annotation, volumeLayerName)
    : getActiveSegmentationTracing(Store.getState());
}

export function hasVolumeTracings(annotation: StoreAnnotation): boolean {
  return annotation.volumes.length > 0;
}

export function getVolumeDescriptors(
  annotation: APIAnnotation | StoreAnnotation | APIAnnotationInfo,
): Array<AnnotationLayerDescriptor> {
  return annotation.annotationLayers.filter((layer) => layer.typ === "Volume");
}

export function getVolumeDescriptorById(
  annotation: APIAnnotation | StoreAnnotation,
  tracingId: string,
): AnnotationLayerDescriptor {
  const descriptors = getVolumeDescriptors(annotation).filter(
    (layer) => layer.tracingId === tracingId,
  );

  if (descriptors.length === 0) {
    throw new Error(`Could not find volume descriptor with id ${tracingId}`);
  }

  return descriptors[0];
}

export function getReadableNameByVolumeTracingId(
  annotation: APIAnnotation | StoreAnnotation,
  tracingId: string,
) {
  const volumeDescriptor = getVolumeDescriptorById(annotation, tracingId);
  return volumeDescriptor.name;
}

export function getSegmentationLayerByHumanReadableName(
  dataset: APIDataset,
  annotation: APIAnnotation | StoreAnnotation,
  name: string,
) {
  try {
    const layer = getSegmentationLayerByName(dataset, name);
    return layer;
  } catch {}

  const layer = getVolumeTracingLayers(dataset).find((currentLayer) => {
    if (currentLayer.tracingId == null) {
      throw new Error("getVolumeTracingLayers must return tracing.");
    }
    const readableName = getReadableNameByVolumeTracingId(annotation, currentLayer.tracingId);
    return readableName === name;
  });

  if (layer == null) {
    throw new Error("Could not find segmentation layer with the name: " + name);
  }

  return layer;
}

export function getAllReadableLayerNames(dataset: APIDataset, store: StoreAnnotation) {
  const allReadableLayerNames = getDataLayers(dataset).map((currentLayer) =>
    "tracingId" in currentLayer && currentLayer.tracingId != null
      ? getReadableNameByVolumeTracingId(store, currentLayer.tracingId)
      : currentLayer.name,
  );
  if (store.skeleton != null) {
    allReadableLayerNames.push("Skeleton");
  }
  return allReadableLayerNames;
}

export function getReadableNameForLayerName(
  dataset: APIDataset,
  tracing: APIAnnotation | StoreAnnotation,
  layerName: string,
): string {
  const layer = getLayerByName(dataset, layerName, true);

  if ("tracingId" in layer && layer.tracingId != null) {
    return getReadableNameByVolumeTracingId(tracing, layer.tracingId);
  } else {
    return layer.name;
  }
}

export function getSegmentationLayerForTracing(
  state: WebknossosState,
  volumeTracing: VolumeTracing,
): APISegmentationLayer {
  return getSegmentationLayerByName(state.dataset, volumeTracing.tracingId);
}

function _getMagInfoOfActiveSegmentationTracingLayer(state: WebknossosState): MagInfo {
  const volumeTracing = getActiveSegmentationTracing(state);

  if (!volumeTracing) {
    return new MagInfo([]);
  }

  const segmentationLayer = getSegmentationLayerForTracing(state, volumeTracing);
  return getMagInfo(segmentationLayer.mags);
}

const getMagInfoOfActiveSegmentationTracingLayer = memoizeOne(
  _getMagInfoOfActiveSegmentationTracingLayer,
);
export function getServerVolumeTracings(
  tracings: Array<ServerTracing> | null | undefined,
): Array<ServerVolumeTracing> {
  const volumeTracings = (tracings || []).filter((tracing) => tracing.typ === "Volume");
  return volumeTracings;
}

export function getActiveCellId(volumeTracing: VolumeTracing): number {
  const { activeCellId } = volumeTracing;
  return activeCellId;
}

export function getActiveUnmappedSegmentId(
  state: WebknossosState,
  volumeTracing: VolumeTracing | null | undefined,
): number | null | undefined {
  if (volumeTracing == null) {
    return null;
  }
  return state.localSegmentationStateByLayer[volumeTracing.tracingId]?.activeUnmappedSegmentId;
}

export function getContourTracingMode(
  state: WebknossosState,
  volumeTracing: VolumeTracing,
): ContourMode {
  return state.localSegmentationStateByLayer[volumeTracing.tracingId]?.contourTracingMode;
}

const MAG_THRESHOLDS_FOR_ZOOM: Partial<Record<AnnotationToolId, number>> = {
  // Note that these are relative to the finest existing mag index.
  // A threshold of 1 indicates that the respective tool can be used in the
  // finest existing magnification as well as the next coarser one.
  [AnnotationTool.TRACE.id]: 1,
  [AnnotationTool.ERASE_TRACE.id]: 1,
  [AnnotationTool.BRUSH.id]: 3,
  [AnnotationTool.ERASE_BRUSH.id]: 3,
  [AnnotationTool.FILL_CELL.id]: 1,
};

export type VolumeAnnotationZoomState =
  | { isDisabled: false }
  | { isDisabled: true; reason: "needs_zoom_in" | "needs_zoom_out" };

export function isVolumeAnnotationDisallowedForZoom(
  tool: AnnotationTool,
  state: WebknossosState,
): VolumeAnnotationZoomState {
  if (state.annotation.volumes.length === 0) {
    // Volume annotation is not possible, but that's not because of an invalid
    // zoom state. The call site should handle such cases differently (e.g.,
    // the toolbar has more disabled-rules for this in place.)
    return { isDisabled: false };
  }

  const threshold = MAG_THRESHOLDS_FOR_ZOOM[tool.id];

  if (threshold == null) {
    // If there is no threshold for the provided tool, it doesn't need to be
    // disabled.
    return { isDisabled: false };
  }

  const activeSegmentation = getActiveSegmentationTracing(state);
  if (!activeSegmentation) {
    // Volume annotation is not possible, but that's not because of an invalid
    // zoom state. Also see volumes.length === 0 for the same reasoning.
    return { isDisabled: false };
  }

  const volumeMags = getMagInfoOfActiveSegmentationTracingLayer(state);
  const finestExistingMagIndex = volumeMags.getFinestMagIndex();

  // Simply check whether the mag currently rendered for the volume tracing layer may be
  // annotated in given the tool's threshold. If it is too coarse, too many voxels would
  // be labeled at once, so the user has to zoom in.
  const renderedVolumeMagIndex = getActiveMagIndexForLayer(state, activeSegmentation.tracingId);
  const isZoomStepTooHigh = renderedVolumeMagIndex > threshold + finestExistingMagIndex;
  return isZoomStepTooHigh ? { isDisabled: true, reason: "needs_zoom_in" } : { isDisabled: false };
}

const MAX_BRUSH_SIZE_FOR_MAG1 = 300;
export function getMaximumBrushSize(state: WebknossosState) {
  const volumeMags = getMagInfoOfActiveSegmentationTracingLayer(state);

  if (volumeMags.mags.length === 0) {
    return MAX_BRUSH_SIZE_FOR_MAG1;
  }

  const finestExistingMagIndex = volumeMags.getFinestMagIndex();
  // For each leading magnification which does not exist,
  // we double the maximum brush size.
  return MAX_BRUSH_SIZE_FOR_MAG1 * 2 ** finestExistingMagIndex;
}

export function getRequestedOrVisibleSegmentationLayer(
  state: WebknossosState,
  layerName: string | null | undefined,
): APISegmentationLayer | null | undefined {
  const requestedLayer =
    layerName != null ? getSegmentationLayerByName(state.dataset, layerName) : null;
  return requestedLayer || getVisibleSegmentationLayer(state);
}

export function getTracingForSegmentationLayer(
  state: WebknossosState,
  layer: APISegmentationLayer,
): VolumeTracing | null | undefined {
  if (layer.tracingId != null) {
    return getVolumeTracingById(state.annotation, layer.tracingId);
  } else {
    return null;
  }
}

export function getRequestedOrDefaultSegmentationTracingLayer(
  state: WebknossosState,
  layerName: string | null | undefined,
): VolumeTracing | null | undefined {
  // If a layerName is passed, the corresponding volume tracing layer is returned.
  // Otherwise:
  //   if there's only one volume tracing layer, return that.
  //   else: return the visible volume tracing layer
  if (layerName != null) {
    const layer = getSegmentationLayerByName(state.dataset, layerName);
    const tracing = getTracingForSegmentationLayer(state, layer);

    if (!tracing) {
      throw new Error(
        "Requested tracing layer is not a tracing, but a disk-based segmentation layer.",
      );
    }

    return tracing;
  }

  if (state.annotation.volumes.length === 1) {
    return state.annotation.volumes[0];
  }

  const visibleLayer = getVisibleSegmentationLayer(state);

  if (visibleLayer == null) {
    return null;
  }

  return getTracingForSegmentationLayer(state, visibleLayer);
}

function _getActiveSegmentationTracing(state: WebknossosState): VolumeTracing | null | undefined {
  return getRequestedOrDefaultSegmentationTracingLayer(state, null);
}

export const getActiveSegmentationTracing = memoizeOne(_getActiveSegmentationTracing);

export function getActiveSegmentationTracingLayer(
  state: WebknossosState,
): APISegmentationLayer | null | undefined {
  const tracing = getRequestedOrDefaultSegmentationTracingLayer(state, null);

  if (!tracing) {
    return null;
  }

  return getSegmentationLayerForTracing(state, tracing);
}

export function enforceActiveVolumeTracing(state: WebknossosState): VolumeTracing {
  const tracing = getActiveSegmentationTracing(state);

  if (tracing == null) {
    throw new Error("No volume annotation is available or enabled.");
  }

  return tracing;
}

export function getRequestedOrVisibleSegmentationLayerEnforced(
  state: WebknossosState,
  layerName: string | null | undefined,
): APISegmentationLayer {
  const effectiveLayer = getRequestedOrVisibleSegmentationLayer(state, layerName);

  if (effectiveLayer != null) {
    return effectiveLayer;
  }

  // If a layerName is passed and invalid, an exception will be raised by getRequestedOrVisibleSegmentationLayer.
  throw new Error(
    "No segmentation layer is currently visible. Pass a valid layerName (you may want to use `getSegmentationLayerName`)",
  );
}

export function getNameOfRequestedOrVisibleSegmentationLayer(
  state: WebknossosState,
  layerName: string | null | undefined,
): string | null | undefined {
  const layer = getRequestedOrVisibleSegmentationLayer(state, layerName);
  return layer != null ? layer.name : null;
}

export function getSegmentsForLayer(state: WebknossosState, layerName: string): SegmentMap {
  const layer = getSegmentationLayerByName(state.dataset, layerName);

  if (layer.tracingId != null) {
    return getVolumeTracingById(state.annotation, layer.tracingId).segments;
  }

  return state.localSegmentationStateByLayer[layer.name].segments;
}

const EMPTY_SEGMENT_GROUPS: SegmentGroup[] = [];
export function getVisibleSegments(state: WebknossosState): {
  segments: SegmentMap | null | undefined;
  segmentGroups: Array<SegmentGroup>;
} {
  const layer = getVisibleSegmentationLayer(state);

  if (layer == null) {
    return { segments: null, segmentGroups: [] };
  }

  if (layer.tracingId != null) {
    const { segments, segmentGroups } = getVolumeTracingById(state.annotation, layer.tracingId);
    return { segments, segmentGroups };
  }

  // There aren't any segment groups for view-only layers
  const { segments } = state.localSegmentationStateByLayer[layer.name];
  return { segments, segmentGroups: EMPTY_SEGMENT_GROUPS };
}

export function getHideUnregisteredSegmentsForVisibleSegmentationLayer(
  state: WebknossosState,
): boolean {
  const layer = getVisibleSegmentationLayer(state);

  if (layer == null) {
    return false;
  }

  return getHideUnregisteredSegmentsForLayer(state, layer.name);
}

export function getHideUnregisteredSegmentsForLayer(
  state: WebknossosState,
  layerName: string,
): boolean {
  return state.localSegmentationStateByLayer[layerName]?.hideUnregisteredSegments;
}

export function getIdReservationsForSegmentationLayer(state: WebknossosState, tracingId: string) {
  return state.localSegmentationStateByLayer[tracingId].idReservations;
}

const EMPTY_SEGMENT_JOURNAL: SegmentJournalEntry[] = [];
export function getSegmentJournalForLayer(
  state: WebknossosState,
  layerName: string,
): SegmentJournalEntry[] {
  const layer = getSegmentationLayerByName(state.dataset, layerName);

  if (layer.tracingId != null) {
    return getVolumeTracingById(state.annotation, layer.tracingId).segmentJournal;
  }

  return EMPTY_SEGMENT_JOURNAL;
}

// Next to returning a clean list of selected segments or group, this method returns
// a callback function that updates the selectedIds in store if segments are stored
// there that are not visible in the segments view tab.
// The returned segment and group ids are all visible in the segments view tab.
function _getSelectedIds(state: WebknossosState): {
  segments: number[];
  group: number | null;
  maybeUpdateStoreAction: (() => void) | null;
} {
  // Ensure that the ids of previously selected segments are removed
  // if these segments aren't visible in the segments tab anymore.
  const nothingSelectedObject = { segments: [], group: null };
  let maybeSetSelectedSegmentsOrGroupsAction = null;
  const visibleSegmentationLayer = getVisibleSegmentationLayer(state);
  if (visibleSegmentationLayer == null) {
    return {
      ...nothingSelectedObject,
      maybeUpdateStoreAction: maybeSetSelectedSegmentsOrGroupsAction,
    };
  }
  const segmentationLayerData = state.localSegmentationStateByLayer[visibleSegmentationLayer.name];
  const { segments, group } = segmentationLayerData.selectedIds;
  if (segments.length === 0 && group == null) {
    return {
      ...nothingSelectedObject,
      maybeUpdateStoreAction: maybeSetSelectedSegmentsOrGroupsAction,
    };
  }
  const currentVisibleSegments = getVisibleSegments(state);
  const currentSegmentIds = new Set(currentVisibleSegments?.segments?.map((segment) => segment.id));
  let cleanedSelectedGroup = null;
  if (group != null) {
    const availableGroups = currentVisibleSegments.segmentGroups.flatMap((group) =>
      getGroupByIdWithSubgroups(currentVisibleSegments.segmentGroups, group.groupId),
    );
    availableGroups.push(MISSING_GROUP_ID);
    cleanedSelectedGroup = availableGroups.includes(group) ? group : null;
  }
  const selectedIds = {
    segments: segments.filter((id) => currentSegmentIds.has(id)),
    group: cleanedSelectedGroup,
  };
  const haveSegmentsOrGroupBeenRemovedFromList =
    selectedIds.segments.length !== segments.length || selectedIds.group !== group;
  if (haveSegmentsOrGroupBeenRemovedFromList) {
    maybeSetSelectedSegmentsOrGroupsAction = () => {
      Store.dispatch(
        setSelectedSegmentsOrGroupAction(
          selectedIds.segments,
          selectedIds.group,
          visibleSegmentationLayer.name,
        ),
      );
    };
  }
  return { ...selectedIds, maybeUpdateStoreAction: maybeSetSelectedSegmentsOrGroupsAction };
}

export const getSelectedIds = reuseInstanceOnEquality(_getSelectedIds);

export function getProofreadingMarkerPosition(state: WebknossosState): Vector3 | null | undefined {
  const layer = getVisibleSegmentationLayer(state);
  if (layer == null) return null;

  return state.localSegmentationStateByLayer[layer.name]?.proofreadingMarkerPosition;
}

/*
  This function returns the magnification and zoom step in which the given segmentation
  tracing layer is currently rendered (if it is rendered). These properties should be used
  when labeling volume data.
 */
function _getRenderableMagForSegmentationTracing(
  state: WebknossosState,
  segmentationTracing: VolumeTracing | null | undefined,
):
  | {
      mag: Vector3;
      zoomStep: number;
    }
  | null
  | undefined {
  if (!segmentationTracing) {
    return null;
  }

  const segmentationLayer = getSegmentationLayerForTracing(state, segmentationTracing);

  const requestedZoomStep = getActiveMagIndexForLayer(state, segmentationLayer.name);
  const { renderMissingDataBlack } = state.datasetConfiguration;
  const magInfo = getMagInfo(segmentationLayer.mags);
  // Check whether the segmentation layer is enabled
  const segmentationSettings = state.datasetConfiguration.layers[segmentationLayer.name];

  if (segmentationSettings.isDisabled) {
    return null;
  }

  // Check whether the requested zoom step exists
  if (magInfo.hasIndex(requestedZoomStep)) {
    return {
      zoomStep: requestedZoomStep,
      mag: magInfo.getMagByIndexOrThrow(requestedZoomStep),
    };
  }

  // Since `renderMissingDataBlack` is enabled, the fallback mags
  // should not be considered.
  if (renderMissingDataBlack) {
    return null;
  }

  // The current mag is missing and fallback rendering
  // is activated. Thus, check whether one of the fallback
  // zoomSteps can be rendered.
  for (
    let fallbackZoomStep = requestedZoomStep + 1;
    fallbackZoomStep <= requestedZoomStep + MAX_ZOOM_STEP_DIFF;
    fallbackZoomStep++
  ) {
    if (magInfo.hasIndex(fallbackZoomStep)) {
      return {
        zoomStep: fallbackZoomStep,
        mag: magInfo.getMagByIndexOrThrow(fallbackZoomStep),
      };
    }
  }

  return null;
}

export const getRenderableMagForSegmentationTracing = reuseInstanceOnEquality(
  _getRenderableMagForSegmentationTracing,
);

function _getRenderableMagForActiveSegmentationTracing(state: WebknossosState):
  | {
      mag: Vector3;
      zoomStep: number;
    }
  | null
  | undefined {
  const activeSegmentationTracing = getActiveSegmentationTracing(state);
  return getRenderableMagForSegmentationTracing(state, activeSegmentationTracing);
}

export const getRenderableMagForActiveSegmentationTracing = reuseInstanceOnEquality(
  _getRenderableMagForActiveSegmentationTracing,
);

export function getMappingInfoForVolumeTracing(
  state: WebknossosState,
  tracingIdOrLayerName: string | null | undefined,
): ActiveMappingInfo {
  return getMappingInfo(state.temporaryConfiguration.activeMappingByLayer, tracingIdOrLayerName);
}

function getVolumeTracingForLayerName(
  state: WebknossosState,
  layerName?: string | null | undefined,
): VolumeTracing | null | undefined {
  if (layerName != null) {
    // This needs to be checked before calling getRequestedOrDefaultSegmentationTracingLayer,
    // as the function will throw an error if layerName is given but a corresponding tracing layer
    // does not exist.
    const layer = getSegmentationLayerByName(state.dataset, layerName);
    const tracing = getTracingForSegmentationLayer(state, layer);

    if (tracing == null) return null;
  }

  const volumeTracing = getRequestedOrDefaultSegmentationTracingLayer(state, layerName);

  return volumeTracing;
}

export function hasEditableMapping(
  state: WebknossosState,
  layerName?: string | null | undefined,
): boolean {
  const volumeTracing = getVolumeTracingForLayerName(state, layerName);

  if (volumeTracing == null) return false;

  return !!volumeTracing.hasEditableMapping;
}

export function isMappingLocked(
  state: WebknossosState,
  layerName?: string | null | undefined,
): boolean {
  const volumeTracing = getVolumeTracingForLayerName(state, layerName);

  if (volumeTracing == null) return false;

  return !!volumeTracing.mappingIsLocked;
}

export function isMappingActivationAllowed(
  state: WebknossosState,
  mappingName: string | null | undefined,
  layerName?: string | null | undefined,
  isMergerModeMapping?: boolean,
): boolean {
  const isEditableMappingActive = hasEditableMapping(state, layerName);
  const isActiveMappingLocked = isMappingLocked(state, layerName);

  const activeMappingInfo = getMappingInfo(
    state.temporaryConfiguration.activeMappingByLayer,
    layerName,
  );

  const isAllowedBecauseOfMergerMode =
    // a merger mode mapping should be enabled
    isMergerModeMapping &&
    // and currently no (or a merger mode) mapping exists
    (activeMappingInfo.mappingName == null || activeMappingInfo.isMergerModeMapping);

  if (!isEditableMappingActive && (!isActiveMappingLocked || isAllowedBecauseOfMergerMode)) {
    return true;
  }

  const volumeTracing = getRequestedOrDefaultSegmentationTracingLayer(state, layerName);

  // This should never be the case, since editable mappings can only be active for volume tracings
  if (volumeTracing == null) return false;

  // Only allow mapping activations of the editable mapping itself if an editable mapping is saved
  // in the volume tracing. Editable mappings cannot be disabled or switched for now.
  return mappingName === volumeTracing.mappingName;
}

export function getEditableMappingForVolumeTracingId(
  state: WebknossosState,
  tracingId: string | null | undefined,
) {
  if (tracingId == null) {
    return null;
  }
  return state.annotation.mappings.find((mapping) => mapping.tracingId === tracingId);
}

export function getLastLabelAction(
  state: WebknossosState,
  volumeTracing: VolumeTracing,
): LabelAction | undefined {
  return state.localSegmentationStateByLayer[volumeTracing.tracingId]?.lastLabelActions[0];
}

export function getLabelActionFromPreviousSlice(
  state: WebknossosState,
  volumeTracing: VolumeTracing,
  mag: Vector3,
  dim: 0 | 1 | 2,
): LabelAction | undefined {
  // Gets the last label action which was performed on a different slice.
  // Note that in coarser mags (e.g., 8-8-2), the comparison of the coordinates
  // is done while respecting how the coordinates are clipped due to that mag.
  const adapt = (vec: Vector3) => V3.roundElementToMag(vec, mag, dim);
  const position = adapt(getFlooredPosition(state.flycam));

  return state.localSegmentationStateByLayer[volumeTracing.tracingId]?.lastLabelActions.find(
    (el) => Math.floor(adapt(el.centroid)[dim]) !== position[dim],
  );
}

export function getSegmentName(
  segment: { id: number; name?: string | undefined | null },
  fallbackToIdOnly: boolean = false,
): string {
  const fallback = fallbackToIdOnly ? `${segment.id}` : `Segment ${segment.id}`;
  // Note that we don't use ?? here because empty strings should also be disregarded.
  return segment.name || fallback;
}

function getMeshOpacity(
  state: WebknossosState,
  segmentId: number,
  layerName: string,
): number | undefined {
  const additionalCoords = state.flycam.additionalCoordinates;
  const additionalCoordinateKey = getAdditionalCoordinatesAsString(additionalCoords);
  const localSegmentationState = state.localSegmentationStateByLayer[layerName];
  if (localSegmentationState?.meshes == null) return undefined;
  const meshData = localSegmentationState.meshes[additionalCoordinateKey];
  if (meshData == null || meshData[segmentId] == null) return undefined;
  return meshData[segmentId].opacity;
}

export function isMeshLoaded(
  state: WebknossosState,
  segmentId: number,
  layerName: string,
): boolean {
  const additionalCoords = state.flycam.additionalCoordinates;
  const additionalCoordinateKey = getAdditionalCoordinatesAsString(additionalCoords);
  const localSegmentationState = state.localSegmentationStateByLayer[layerName];
  if (localSegmentationState?.meshes == null) return false;
  const meshData = localSegmentationState.meshes[additionalCoordinateKey];
  if (meshData == null || meshData[segmentId] == null) return false;
  return meshData[segmentId] != null;
}

export function getAllLoadedMeshes(state: WebknossosState, layerName: string): Set<number> {
  const loadedMeshIds = new Set<number>();
  const additionalCoords = state.flycam.additionalCoordinates;
  const additionalCoordinateKey = getAdditionalCoordinatesAsString(additionalCoords);
  const localSegmentationState = state.localSegmentationStateByLayer[layerName];
  if (localSegmentationState?.meshes == null) return loadedMeshIds;
  const meshData = localSegmentationState.meshes[additionalCoordinateKey];
  if (meshData == null) return loadedMeshIds;
  Object.values(meshData).forEach((meshInfo) => {
    loadedMeshIds.add(meshInfo.segmentId);
  });
  return loadedMeshIds;
}

// Output is in [0,1] for R, G, B, and A
export function getSegmentColorAsRGBA(
  state: WebknossosState,
  mappedId: number,
  layerName?: string | null | undefined,
): Vector4 {
  const segmentationLayer = getRequestedOrVisibleSegmentationLayer(state, layerName);
  if (!segmentationLayer) {
    return [1, 1, 1, 1];
  }

  const segments = getSegmentsForLayer(state, segmentationLayer.name);
  if (segments) {
    const segment = segments.getNullable(mappedId);

    if (segment?.color) {
      const [r, g, b] = segment.color;
      const maybeMeshOpacity = getMeshOpacity(state, mappedId, segmentationLayer.name);
      return [r, g, b, maybeMeshOpacity ?? Constants.DEFAULT_MESH_OPACITY];
    }
  }

  return jsConvertCellIdToRGBA(mappedId);
}

// Output is in [0,1] for H, S, L, and A
export function getSegmentColorAsHSLA(
  state: WebknossosState,
  mappedId: number,
  layerName?: string | null | undefined,
): Vector4 {
  const [r, g, b, a] = getSegmentColorAsRGBA(state, mappedId, layerName);
  const [hue, saturation, value] = jsRgb2hsl([r, g, b]);
  return [hue, saturation, value, a];
}

const AGGLOMERATE_STATES = {
  NO_SEGMENTATION: {
    value: false,
    reason: "A segmentation layer needs to be visible to load an agglomerate tree.",
  },
  NO_MAPPING: {
    value: false,
    reason: messages["tracing.agglomerate_tree.no_mapping"],
  },
  NO_AGGLOMERATE_FILE_ACTIVE: {
    value: false,
    reason: messages["tracing.agglomerate_tree.no_agglomerate_file_active"],
  },
  NO_AGGLOMERATE_FILE_AVAILABLE: {
    value: false,
    reason: messages["tracing.agglomerate_tree.no_agglomerate_file_available"],
  },
  NO_AGGLOMERATE_FILES_LOADED_YET: {
    value: false,
    reason: messages["tracing.agglomerate_tree.no_agglomerate_files_loaded_yet"],
  },
  YES: {
    value: true,
    reason: "",
  },
};

export type AgglomerateState = (typeof AGGLOMERATE_STATES)[keyof typeof AGGLOMERATE_STATES];

export function hasAgglomerateMapping(state: WebknossosState) {
  const segmentation = getVisibleSegmentationLayer(state);

  if (!segmentation) {
    return AGGLOMERATE_STATES.NO_SEGMENTATION;
  }

  if (segmentation.agglomerates == null) {
    return AGGLOMERATE_STATES.NO_AGGLOMERATE_FILES_LOADED_YET;
  }

  if (segmentation.agglomerates.length === 0) {
    return AGGLOMERATE_STATES.NO_AGGLOMERATE_FILE_AVAILABLE;
  }

  const { mappingName, mappingType, mappingStatus } = getMappingInfo(
    state.temporaryConfiguration.activeMappingByLayer,
    segmentation.name,
  );

  if (mappingName == null || mappingStatus !== MappingStatusEnum.ENABLED) {
    return AGGLOMERATE_STATES.NO_MAPPING;
  }

  if (mappingType !== "HDF5") {
    return AGGLOMERATE_STATES.NO_AGGLOMERATE_FILE_ACTIVE;
  }

  return AGGLOMERATE_STATES.YES;
}

export function isZoomThresholdExceededForAgglomerateMapping(
  state: WebknossosState,
  segmentationLayerName: string,
) {
  return (
    getActiveMagIndexForLayer(state, segmentationLayerName) >
    Math.log2(MAX_MAG_FOR_AGGLOMERATE_MAPPING)
  );
}

export function getMeshesForAdditionalCoordinates(
  state: WebknossosState,
  additionalCoordinates: AdditionalCoordinate[] | null | undefined,
  layerName: string,
) {
  const addCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);
  const meshRecords = state.localSegmentationStateByLayer[layerName].meshes;
  if (meshRecords?.[addCoordKey] != null) {
    return meshRecords[addCoordKey];
  }
  return null;
}

export function getMeshesForCurrentAdditionalCoordinates(
  state: WebknossosState,
  layerName: string,
) {
  return getMeshesForAdditionalCoordinates(state, state.flycam.additionalCoordinates, layerName);
}

export function getMeshInfoForSegment(
  state: WebknossosState,
  additionalCoordinates: AdditionalCoordinate[] | null,
  layerName: string,
  segmentId: number,
) {
  const meshesForAddCoords = getMeshesForAdditionalCoordinates(
    state,
    additionalCoordinates,
    layerName,
  );
  if (meshesForAddCoords == null) return null;
  return meshesForAddCoords[segmentId];
}

export function needsLocalHdf5Mapping(state: WebknossosState, layerName: string) {
  const volumeTracing = getVolumeTracingByLayerName(state.annotation, layerName);
  if (volumeTracing == null) {
    return false;
  }

  return (
    // An annotation that has an editable mapping is likely proofread a lot.
    // Switching between tools should not require a reload which is why
    // needsLocalHdf5Mapping() will always return true in that case.
    volumeTracing.hasEditableMapping || state.uiInformation.activeTool === AnnotationTool.PROOFREAD
  );
}

export type BucketRetrievalSource =
  | ["REQUESTED-WITHOUT-MAPPING", "NO-LOCAL-MAPPING-APPLIED"]
  | ["REQUESTED-WITHOUT-MAPPING", "LOCAL-MAPPING-APPLIED", string]
  | ["REQUESTED-WITH-MAPPING", string];

export const getBucketRetrievalSourceFn =
  // The function that is passed to memoize will only be executed once
  // per layerName. This is important since the function uses reuseInstanceOnEquality
  // to create a function that ensures that identical BucketRetrievalSource tuples will be reused between
  // consecutive calls.
  memoize((layerName: string) =>
    reuseInstanceOnEquality((state: WebknossosState): BucketRetrievalSource => {
      const usesLocalHdf5Mapping = needsLocalHdf5Mapping(state, layerName);

      const mappingInfo = getMappingInfoForVolumeTracing(state, layerName);

      if (
        mappingInfo.mappingStatus === MappingStatusEnum.DISABLED ||
        mappingInfo.mappingName == null
      ) {
        return ["REQUESTED-WITHOUT-MAPPING", "NO-LOCAL-MAPPING-APPLIED"];
      }

      if (usesLocalHdf5Mapping || mappingInfo.mappingType === "JSON") {
        return ["REQUESTED-WITHOUT-MAPPING", "LOCAL-MAPPING-APPLIED", mappingInfo.mappingName];
      }

      return ["REQUESTED-WITH-MAPPING", mappingInfo.mappingName];
    }),
  );

export function getReadableNameOfVolumeLayer(
  layer: APIDataLayer,
  tracing: APIAnnotation | StoreAnnotation,
): string | null {
  return "tracingId" in layer && layer.tracingId != null
    ? getReadableNameByVolumeTracingId(tracing, layer.tracingId)
    : null;
}

export function getCurrentMappingName(state: WebknossosState) {
  const visibleSegmentationLayer = getVisibleSegmentationLayer(state);
  const volumeTracing = getActiveSegmentationTracing(state);
  if (volumeTracing?.mappingName != null) return volumeTracing?.mappingName;
  const mappingInfo = getMappingInfo(
    state.temporaryConfiguration.activeMappingByLayer,
    visibleSegmentationLayer?.name,
  );
  return mappingInfo.mappingName;
}
